import { createCollectionStore } from "../collection/collection-store.js";
import { createStore, setup, sync } from "@ariakit/store";
import { defaultValue, flatten2DArray, reverseArray } from "@ariakit/utils";
//#region src/composite/composite-store.ts
const NULL_ITEM = { id: null };
/**
* Finds the first enabled item.
*/
function findFirstEnabledItem(items, excludeId) {
	return items.find((item) => {
		if (excludeId) return !item.disabled && item.id !== excludeId;
		return !item.disabled;
	});
}
function getEnabledItems(items, excludeId) {
	return items.filter((item) => {
		if (excludeId) return !item.disabled && item.id !== excludeId;
		return !item.disabled;
	});
}
function getItemsInRow(items, rowId) {
	return items.filter((item) => item.rowId === rowId);
}
function findEnabledItemId({ items, fromIndex, step, rowId, excludeId }) {
	for (let i = fromIndex; i >= 0 && i < items.length; i += step) {
		const item = items[i];
		if (!item) continue;
		if (item.rowId !== rowId) continue;
		if (item.disabled) continue;
		if (excludeId != null && item.id === excludeId) continue;
		return item.id;
	}
}
/**
* Moves all the items before the passed `id` to the end of the array. This is
* useful when we want to loop through the items in the same row or column as
* the first items will be placed after the last items.
*
* The null item that's inserted when `shouldInsertNullItem` is set to `true`
* represents the composite container itself. When the active item is null, the
* composite container has focus.
*/
function flipItems(items, activeId, shouldInsertNullItem = false) {
	const index = items.findIndex((item) => item.id === activeId);
	return [
		...items.slice(index + 1),
		...shouldInsertNullItem ? [NULL_ITEM] : [],
		...items.slice(0, index)
	];
}
/**
* Creates a two-dimensional array with items grouped by their rowId's.
*/
function groupItemsByRows(items) {
	const rows = [];
	for (const item of items) {
		const row = rows.find((currentRow) => currentRow[0]?.rowId === item.rowId);
		if (row) row.push(item);
		else rows.push([item]);
	}
	return rows;
}
function getMaxRowLength(array) {
	let maxLength = 0;
	for (const { length } of array) if (length > maxLength) maxLength = length;
	return maxLength;
}
function createEmptyItem(rowId) {
	return {
		id: "__EMPTY_ITEM__",
		disabled: true,
		rowId
	};
}
function normalizeRows(rows, activeId, focusShift) {
	const maxLength = getMaxRowLength(rows);
	for (const row of rows) for (let i = 0; i < maxLength; i += 1) {
		const item = row[i];
		if (!item || focusShift && item.disabled) {
			const previousItem = i === 0 && focusShift ? findFirstEnabledItem(row) : row[i - 1];
			row[i] = previousItem && activeId !== previousItem.id && focusShift ? previousItem : createEmptyItem(previousItem?.rowId);
		}
	}
	return rows;
}
function verticalizeItems(items) {
	const rows = groupItemsByRows(items);
	const maxLength = getMaxRowLength(rows);
	const verticalized = [];
	for (let i = 0; i < maxLength; i += 1) for (const row of rows) {
		const item = row[i];
		if (item) verticalized.push({
			...item,
			rowId: item.rowId ? `${i}` : void 0
		});
	}
	return verticalized;
}
/**
* Creates a composite store.
*/
function createCompositeStore(props = {}) {
	const syncState = props.store?.getState();
	const collection = createCollectionStore(props);
	const activeId = defaultValue(props.activeId, syncState?.activeId, props.defaultActiveId);
	const composite = createStore({
		...collection.getState(),
		id: defaultValue(props.id, syncState?.id) ?? `id-${Math.random().toString(36).slice(2, 8)}`,
		activeId,
		baseElement: defaultValue(syncState?.baseElement, null),
		includesBaseElement: defaultValue(props.includesBaseElement, syncState?.includesBaseElement, activeId === null),
		moves: defaultValue(syncState?.moves, 0),
		orientation: defaultValue(props.orientation, syncState?.orientation, "both"),
		rtl: defaultValue(props.rtl, syncState?.rtl, false),
		virtualFocus: defaultValue(props.virtualFocus, syncState?.virtualFocus, false),
		focusLoop: defaultValue(props.focusLoop, syncState?.focusLoop, false),
		focusWrap: defaultValue(props.focusWrap, syncState?.focusWrap, false),
		focusShift: defaultValue(props.focusShift, syncState?.focusShift, false)
	}, collection, props.store);
	setup(composite, () => sync(composite, ["renderedItems", "activeId"], (state) => {
		composite.setState("activeId", (activeId) => {
			if (activeId !== void 0) return activeId;
			return findFirstEnabledItem(state.renderedItems)?.id;
		});
	}));
	const getNextId = (direction = "next", options = {}) => {
		const defaultState = composite.getState();
		const { skip = 0, activeId = defaultState.activeId, focusShift = defaultState.focusShift, focusLoop = defaultState.focusLoop, focusWrap = defaultState.focusWrap, includesBaseElement = defaultState.includesBaseElement, renderedItems = defaultState.renderedItems, rtl = defaultState.rtl } = options;
		const isVerticalDirection = direction === "up" || direction === "down";
		const isNextDirection = direction === "next" || direction === "down";
		const canReverse = isNextDirection ? rtl && !isVerticalDirection : !rtl || isVerticalDirection;
		const canShift = focusShift && !skip;
		if (!skip && !focusWrap && !includesBaseElement && activeId != null) {
			if (!isVerticalDirection ? true : !canShift && !renderedItems.some((item) => item.rowId != null)) {
				const activeIndex = renderedItems.findIndex((item) => item.id === activeId);
				const activeItem = renderedItems[activeIndex];
				if (activeItem) {
					const step = canReverse ? -1 : 1;
					const nextId = findEnabledItemId({
						items: renderedItems,
						fromIndex: activeIndex + step,
						step,
						rowId: activeItem.rowId,
						excludeId: activeId
					});
					if (nextId !== void 0) return nextId;
					if (!(focusLoop && (isVerticalDirection ? focusLoop !== "horizontal" : focusLoop !== "vertical"))) return void 0;
					return findEnabledItemId({
						items: renderedItems,
						fromIndex: step === 1 ? 0 : renderedItems.length - 1,
						step,
						rowId: activeItem.rowId,
						excludeId: activeId
					});
				}
			}
		}
		let items = !isVerticalDirection ? renderedItems : flatten2DArray(normalizeRows(groupItemsByRows(renderedItems), activeId, canShift));
		items = canReverse ? reverseArray(items) : items;
		items = isVerticalDirection ? verticalizeItems(items) : items;
		if (activeId == null) return findFirstEnabledItem(items)?.id;
		const activeItem = items.find((item) => item.id === activeId);
		if (!activeItem) return findFirstEnabledItem(items)?.id;
		const isGrid = items.some((item) => item.rowId);
		const activeIndex = items.indexOf(activeItem);
		const nextItems = items.slice(activeIndex + 1);
		const nextItemsInRow = getItemsInRow(nextItems, activeItem.rowId);
		if (skip) {
			const nextEnabledItemsInRow = getEnabledItems(nextItemsInRow, activeId);
			return (nextEnabledItemsInRow.slice(skip)[0] || nextEnabledItemsInRow[nextEnabledItemsInRow.length - 1])?.id;
		}
		const canLoop = focusLoop && (isVerticalDirection ? focusLoop !== "horizontal" : focusLoop !== "vertical");
		const canWrap = isGrid && focusWrap && (isVerticalDirection ? focusWrap !== "horizontal" : focusWrap !== "vertical");
		const hasNullItem = isNextDirection ? (!isGrid || isVerticalDirection) && canLoop && includesBaseElement : isVerticalDirection ? includesBaseElement : false;
		if (canLoop) return findFirstEnabledItem(flipItems(canWrap && !hasNullItem ? items : getItemsInRow(items, activeItem.rowId), activeId, hasNullItem), activeId)?.id;
		if (canWrap) {
			const nextItem = findFirstEnabledItem(hasNullItem ? nextItemsInRow : nextItems, activeId);
			return hasNullItem ? nextItem?.id || null : nextItem?.id;
		}
		const nextItem = findFirstEnabledItem(nextItemsInRow, activeId);
		if (!nextItem && hasNullItem) return null;
		return nextItem?.id;
	};
	const getNextIdFromOptions = (direction, options) => {
		if (typeof options === "number") return getNextId(direction, { skip: options });
		return getNextId(direction, options);
	};
	return {
		...collection,
		...composite,
		setBaseElement: (element) => composite.setState("baseElement", element),
		setActiveId: (id) => composite.setState("activeId", id),
		move: (id) => {
			if (id === void 0) return;
			composite.setState("activeId", id);
			composite.setState("moves", (moves) => moves + 1);
		},
		first: () => findFirstEnabledItem(composite.getState().renderedItems)?.id,
		last: () => findFirstEnabledItem(reverseArray(composite.getState().renderedItems))?.id,
		next: (options) => getNextIdFromOptions("next", options),
		previous: (options) => getNextIdFromOptions("previous", options),
		down: (options) => getNextIdFromOptions("down", options),
		up: (options) => getNextIdFromOptions("up", options)
	};
}
//#endregion
export { NULL_ITEM, createCompositeStore, findFirstEnabledItem, flipItems, groupItemsByRows };

//# sourceMappingURL=composite-store.js.map